/**
 * React Query hooks for the RoyaltyFlow Pro API.
 *
 * Thin, typed wrappers: every hook resolves the auth token from context and
 * stays disabled until a session exists. List hooks return `{ data, meta }`
 * so pagination survives the envelope unwrap.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from './auth';
import { apiFetch, apiFetchWithMeta, buildQuery } from './client';
import type { ApiFetchOptions, PaginatedResult } from './client';
import type {
  ApprovedReportView,
  AvailabilityFilters,
  AvailabilityResult,
  BulkCreateResult,
  CatalogMetrics,
  ContractListItemView,
  ContractMetrics,
  ContractPartyView,
  ContractStatus,
  ContractType,
  ContractView,
  CreateContractInput,
  CreateCustomerInput,
  CreateDealInput,
  CreatePartyInput,
  CreateRightInput,
  CreateSimulationInput,
  CreateSeasonInput,
  CreateShowInput,
  CrmDashboard,
  CustomerView,
  DealStage,
  DealView,
  DocumentView,
  FinancialDashboard,
  ImportSalesInput,
  InvitationView,
  ManualResidualInput,
  ManualShareInput,
  MatrixColumnDimension,
  MatrixRowDimension,
  MatrixView,
  OrgUserView,
  PartyView,
  PresignUploadResult,
  ReportDownloadView,
  ResidualMetrics,
  ResidualView,
  RightsDashboard,
  RightStatus,
  RightType,
  RightView,
  RoleView,
  SalesImportResult,
  SeasonView,
  ShareMetrics,
  ShareView,
  ShowView,
  SimulationView,
  SimulationDetailView,
  StartExtractionResult,
  UpdateContractInput,
  UpdateCustomerInput,
  UpdateDealInput,
  UpdateShareInput,
} from './types';

export interface PageQuery {
  page?: number;
  limit?: number;
}

export interface ListShowsFilters extends PageQuery {
  search?: string;
}

export interface ListContractsFilters extends PageQuery {
  search?: string;
  contractType?: ContractType;
  status?: ContractStatus;
}

export interface ListRightsFilters extends PageQuery {
  showId?: string;
  seasonId?: string;
  mediaType?: string;
  territory?: string;
  rightType?: RightType;
  status?: RightStatus;
}

export interface ListCustomersFilters extends PageQuery {
  search?: string;
}

export interface ListDealsFilters extends PageQuery {
  stage?: DealStage;
  customerId?: string;
  salesExecId?: string;
  search?: string;
}

export interface ListOrgUsersFilters extends PageQuery {
  search?: string;
}

/**
 * Token-aware mutation wrapper: runs `request` with the session token and
 * invalidates every listed query-key prefix on success.
 */
function useApiMutation<TInput, TResult>(
  request: (input: TInput, token: string | null) => Promise<TResult>,
  invalidate: readonly (readonly unknown[])[],
) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: TInput) => request(input, token),
    onSettled: () => {
      invalidate.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [...key] });
      });
    },
  });
}

function useAuthedQuery<T>(key: readonly unknown[], path: string) {
  const { token } = useAuth();

  return useQuery<T>({
    queryKey: key,
    queryFn: ({ signal }) => apiFetch<T>(path, { token, signal }),
    enabled: token !== null,
  });
}

function useAuthedListQuery<T>(key: readonly unknown[], path: string) {
  const { token } = useAuth();

  return useQuery<PaginatedResult<T>>({
    queryKey: key,
    queryFn: ({ signal }) => apiFetchWithMeta<T>(path, { token, signal }),
    enabled: token !== null,
  });
}

// --- shows ---

export function useShows(filters: ListShowsFilters = {}) {
  return useAuthedListQuery<ShowView[]>(['shows', filters], `/shows${buildQuery(filters)}`);
}

export function useShow(id: string | undefined) {
  const { token } = useAuth();

  return useQuery<ShowView>({
    queryKey: ['shows', id],
    queryFn: ({ signal }) => apiFetch<ShowView>(`/shows/${id}`, { token, signal }),
    enabled: token !== null && id !== undefined,
  });
}

export function useShowMetrics() {
  return useAuthedQuery<CatalogMetrics>(['shows', 'metrics'], '/shows/metrics');
}

// --- contracts ---

export function useContracts(filters: ListContractsFilters = {}) {
  return useAuthedListQuery<ContractListItemView[]>(
    ['contracts', filters],
    `/contracts${buildQuery(filters)}`,
  );
}

export function useContract(id: string | undefined) {
  const { token } = useAuth();

  return useQuery<ContractView>({
    queryKey: ['contracts', id],
    queryFn: ({ signal }) => apiFetch<ContractView>(`/contracts/${id}`, { token, signal }),
    enabled: token !== null && id !== undefined,
  });
}

export interface ContractMetricsFilters {
  status?: ContractStatus;
}

export function useContractMetrics(filters: ContractMetricsFilters = {}) {
  return useAuthedQuery<ContractMetrics>(
    ['contracts', 'metrics', filters],
    `/contracts/metrics${buildQuery(filters)}`,
  );
}

export function useAuthorizeContract() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contractId: string) =>
      apiFetch<ContractView>(`/contracts/${contractId}/authorize`, { method: 'POST', token }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
    },
  });
}

// --- shares & residuals (nested under a contract) ---

export function useShares(contractId: string | undefined) {
  const { token } = useAuth();

  return useQuery<ShareView[]>({
    queryKey: ['contracts', contractId, 'shares'],
    queryFn: ({ signal }) =>
      apiFetch<ShareView[]>(`/contracts/${contractId}/backend-shares`, { token, signal }),
    enabled: token !== null && contractId !== undefined,
  });
}

export function useResiduals(contractId: string | undefined) {
  const { token } = useAuth();

  return useQuery<ResidualView[]>({
    queryKey: ['contracts', contractId, 'residuals'],
    queryFn: ({ signal }) =>
      apiFetch<ResidualView[]>(`/contracts/${contractId}/residuals`, { token, signal }),
    enabled: token !== null && contractId !== undefined,
  });
}

// --- sales ---

export function useSalesImport() {
  return useApiMutation<ImportSalesInput, SalesImportResult>(
    (input, token) =>
      apiFetch<SalesImportResult>('/sales-data/imports', { method: 'POST', body: input, token }),
    // Imports create draft simulations under contracts.
    [['sales'], ['contracts'], ['simulations']],
  );
}

// --- rights ---

export function useRightsDashboard() {
  return useAuthedQuery<RightsDashboard>(['rights', 'dashboard'], '/rights/dashboard');
}

export function useRights(filters: ListRightsFilters = {}) {
  return useAuthedListQuery<RightView[]>(['rights', filters], `/rights${buildQuery(filters)}`);
}

// --- crm ---

export function useCrmDashboard() {
  return useAuthedQuery<CrmDashboard>(['crm', 'dashboard'], '/crm/dashboard');
}

export function useCustomers(filters: ListCustomersFilters = {}) {
  return useAuthedListQuery<CustomerView[]>(
    ['crm', 'customers', filters],
    `/crm/customers${buildQuery(filters)}`,
  );
}

export function useDeals(filters: ListDealsFilters = {}) {
  return useAuthedListQuery<DealView[]>(
    ['crm', 'deals', filters],
    `/crm/deals${buildQuery(filters)}`,
  );
}

// --- org users (admin) ---

export function useOrgUsers(filters: ListOrgUsersFilters = {}) {
  return useAuthedListQuery<OrgUserView[]>(
    ['org', 'users', filters],
    `/org/users${buildQuery(filters)}`,
  );
}

export function useOrgRoles() {
  return useAuthedQuery<RoleView[]>(['org', 'roles'], '/org/roles');
}

// --- approved reports ---

export function useApprovedReports(filters: PageQuery = {}) {
  return useAuthedListQuery<ApprovedReportView[]>(
    ['reports', 'approved', filters],
    `/approved-reports${buildQuery(filters)}`,
  );
}

export function useFinancialDashboard() {
  return useAuthedQuery<FinancialDashboard>(
    ['reports', 'dashboard'],
    '/approved-reports/dashboard',
  );
}

export function useApproveReport() {
  return useApiMutation<string, ApprovedReportView>(
    (simulationId, token) =>
      apiFetch<ApprovedReportView>(`/approved-reports/approve/${simulationId}`, {
        method: 'POST',
        token,
      }),
    [['reports'], ['contracts'], ['simulations']],
  );
}

/** Imperative download-URL fetch; the caller opens the returned URL. */
export function useReportDownload() {
  return useApiMutation<string, ReportDownloadView>(
    (reportId, token) =>
      apiFetch<ReportDownloadView>(`/approved-reports/${reportId}/download`, { token }),
    [],
  );
}

// --- documents (presign → PUT bytes → finalize, as one mutation) ---

export function useUploadDocument() {
  return useApiMutation<{ file: File; mimeType: string }, DocumentView>(
    async ({ file, mimeType }, token) => {
      const presign = await apiFetch<PresignUploadResult>('/documents/presign', {
        method: 'POST',
        body: { fileName: file.name, mimeType, fileSize: file.size },
        token,
      });

      const put = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': mimeType },
        body: file,
      });

      if (!put.ok) {
        throw new Error(`File upload failed with status ${put.status}`);
      }

      return apiFetch<DocumentView>(`/documents/${presign.document.id}/finalize`, {
        method: 'POST',
        body: {},
        token,
      });
    },
    [['documents']],
  );
}

// --- contract mutations & extraction ---

export function useCreateContract() {
  return useApiMutation<CreateContractInput, ContractView>(
    (input, token) => apiFetch<ContractView>('/contracts', { method: 'POST', body: input, token }),
    [['contracts'], ['shows', 'metrics']],
  );
}

export function useUpdateContract() {
  return useApiMutation<{ contractId: string; input: UpdateContractInput }, ContractView>(
    ({ contractId, input }, token) =>
      apiFetch<ContractView>(`/contracts/${contractId}`, { method: 'PATCH', body: input, token }),
    [['contracts']],
  );
}

export function useDeleteContract() {
  return useApiMutation<string, void>(
    (contractId, token) => apiFetch<void>(`/contracts/${contractId}`, { method: 'DELETE', token }),
    [['contracts'], ['shows', 'metrics']],
  );
}

export function useContractParties(contractId: string | undefined) {
  const { token } = useAuth();

  return useQuery<ContractPartyView[]>({
    queryKey: ['contracts', contractId, 'parties'],
    queryFn: ({ signal }) =>
      apiFetch<ContractPartyView[]>(`/contracts/${contractId}/parties`, { token, signal }),
    enabled: token !== null && contractId !== undefined,
  });
}

export function useLinkParty() {
  return useApiMutation<{ contractId: string; partyId: string; role?: string }, ContractPartyView>(
    ({ contractId, partyId, role }, token) =>
      apiFetch<ContractPartyView>(`/contracts/${contractId}/parties`, {
        method: 'POST',
        body: { partyId, ...(role !== undefined ? { role } : {}) },
        token,
      }),
    [['contracts']],
  );
}

export function useStartExtraction() {
  return useApiMutation<{ contractId: string; documentId: string }, StartExtractionResult>(
    ({ contractId, documentId }, token) =>
      apiFetch<StartExtractionResult>(`/contracts/${contractId}/extractions`, {
        method: 'POST',
        body: { documentId },
        token,
      }),
    [['contracts'], ['parties'], ['shows']],
  );
}

// --- shows & seasons mutations ---

export function useCreateShow() {
  return useApiMutation<CreateShowInput, ShowView>(
    (input, token) => apiFetch<ShowView>('/shows', { method: 'POST', body: input, token }),
    [['shows']],
  );
}

export function useCreateSeason() {
  return useApiMutation<{ showId: string; input: CreateSeasonInput }, SeasonView>(
    ({ showId, input }, token) =>
      apiFetch<SeasonView>(`/shows/${showId}/seasons`, { method: 'POST', body: input, token }),
    [['shows']],
  );
}

// --- parties ---

export function useParties(filters: ListShowsFilters = {}) {
  return useAuthedListQuery<PartyView[]>(['parties', filters], `/parties${buildQuery(filters)}`);
}

export function useCreateParty() {
  return useApiMutation<CreatePartyInput, PartyView>(
    (input, token) => apiFetch<PartyView>('/parties', { method: 'POST', body: input, token }),
    [['parties']],
  );
}

// --- share & residual mutations (contract-scoped keys share the prefix) ---

export function useShareMetrics(contractId: string | undefined) {
  const { token } = useAuth();

  return useQuery<ShareMetrics>({
    queryKey: ['contracts', contractId, 'share-metrics'],
    queryFn: ({ signal }) =>
      apiFetch<ShareMetrics>(`/contracts/${contractId}/backend-shares/metrics`, { token, signal }),
    enabled: token !== null && contractId !== undefined,
  });
}

export function useAddShares() {
  return useApiMutation<{ contractId: string; shares: ManualShareInput[] }, ShareView[]>(
    ({ contractId, shares }, token) =>
      apiFetch<ShareView[]>(`/contracts/${contractId}/backend-shares`, {
        method: 'POST',
        body: { shares },
        token,
      }),
    [['contracts']],
  );
}

export function useImportSharesCsv() {
  return useApiMutation<{ contractId: string; csvText: string; fileName?: string }, ShareView[]>(
    ({ contractId, csvText, fileName }, token) =>
      apiFetch<ShareView[]>(`/contracts/${contractId}/backend-shares/csv`, {
        method: 'POST',
        body: { csvText, fileName },
        token,
      }),
    [['contracts']],
  );
}

/** Corrects a single share row in place — e.g. a wrong AI-extracted value. */
export function useUpdateShare() {
  return useApiMutation<
    { contractId: string; shareId: string; input: UpdateShareInput },
    ShareView
  >(
    ({ contractId, shareId, input }, token) =>
      apiFetch<ShareView>(`/contracts/${contractId}/backend-shares/${shareId}`, {
        method: 'PATCH',
        body: input,
        token,
      }),
    [['contracts']],
  );
}

/** Removes a single wrong share row without replacing the whole set. */
export function useDeleteShare() {
  return useApiMutation<{ contractId: string; shareId: string }, { id: string }>(
    ({ contractId, shareId }, token) =>
      apiFetch<{ id: string }>(`/contracts/${contractId}/backend-shares/${shareId}`, {
        method: 'DELETE',
        token,
      }),
    [['contracts']],
  );
}

export function useResidualMetrics(contractId: string | undefined) {
  const { token } = useAuth();

  return useQuery<ResidualMetrics>({
    queryKey: ['contracts', contractId, 'residual-metrics'],
    queryFn: ({ signal }) =>
      apiFetch<ResidualMetrics>(`/contracts/${contractId}/residuals/metrics`, { token, signal }),
    enabled: token !== null && contractId !== undefined,
  });
}

export function useAddResiduals() {
  return useApiMutation<{ contractId: string; residuals: ManualResidualInput[] }, ResidualView[]>(
    ({ contractId, residuals }, token) =>
      apiFetch<ResidualView[]>(`/contracts/${contractId}/residuals`, {
        method: 'POST',
        body: { residuals },
        token,
      }),
    [['contracts']],
  );
}

export function useImportResidualsCsv() {
  return useApiMutation<{ contractId: string; csvText: string; fileName?: string }, ResidualView[]>(
    ({ contractId, csvText, fileName }, token) =>
      apiFetch<ResidualView[]>(`/contracts/${contractId}/residuals/csv`, {
        method: 'POST',
        body: { csvText, fileName },
        token,
      }),
    [['contracts']],
  );
}

/** PUT semantics — replaces every residual on the contract (override). */
export function useOverrideResiduals() {
  return useApiMutation<{ contractId: string; residuals: ManualResidualInput[] }, ResidualView[]>(
    ({ contractId, residuals }, token) =>
      apiFetch<ResidualView[]>(`/contracts/${contractId}/residuals`, {
        method: 'PUT',
        body: { residuals },
        token,
      }),
    [['contracts']],
  );
}

// --- simulations ---

export function useContractSimulations(contractId: string | undefined) {
  const { token } = useAuth();

  return useQuery<SimulationView[]>({
    queryKey: ['contracts', contractId, 'simulations'],
    queryFn: ({ signal }) =>
      apiFetch<SimulationView[]>(`/contracts/${contractId}/simulations`, { token, signal }),
    enabled: token !== null && contractId !== undefined,
  });
}

export function useCreateSimulation() {
  return useApiMutation<CreateSimulationInput, SimulationView>(
    ({ contractId, ...input }, token) =>
      apiFetch<SimulationView>(`/contracts/${contractId}/simulations`, {
        method: 'POST',
        body: input,
        token,
      }),
    [['contracts']],
  );
}

export function useSimulation(id: string) {
  return useAuthedQuery<SimulationDetailView>(['simulations', id], `/simulations/${id}`);
}

export function useDeleteSimulation() {
  return useApiMutation<string, void>(
    (id, token) => apiFetch<void>(`/simulations/${id}`, { method: 'DELETE', token }),
    [['contracts'], ['simulations']],
  );
}

export function useRunSimulation() {
  return useApiMutation<
    { simulationId: string; periodStart: string; periodEnd: string },
    SimulationDetailView
  >(
    ({ simulationId, ...period }, token) =>
      apiFetch<SimulationDetailView>(`/simulations/${simulationId}/run`, {
        method: 'POST',
        body: period,
        token,
      }),
    [['contracts'], ['simulations']],
  );
}

// --- rights mutations, matrix, availability ---

export function useCreateRight() {
  return useApiMutation<CreateRightInput, RightView>(
    (input, token) => apiFetch<RightView>('/rights', { method: 'POST', body: input, token }),
    [['rights']],
  );
}

export function useBulkCreateRights() {
  return useApiMutation<CreateRightInput[], BulkCreateResult>(
    (rights, token) =>
      apiFetch<BulkCreateResult>('/rights/bulk', { method: 'POST', body: { rights }, token }),
    [['rights']],
  );
}

export function useRightsMatrix(rows: MatrixRowDimension, columns: MatrixColumnDimension) {
  return useAuthedQuery<MatrixView>(
    ['rights', 'matrix', rows, columns],
    `/rights/matrix${buildQuery({ rows, columns })}`,
  );
}

export function useRightsAvailability(filters: AvailabilityFilters | null) {
  const { token } = useAuth();

  return useQuery<AvailabilityResult[]>({
    queryKey: ['rights', 'availability', filters],
    queryFn: ({ signal }) =>
      apiFetch<AvailabilityResult[]>(`/rights/availability${buildQuery(filters ?? {})}`, {
        token,
        signal,
      }),
    enabled: token !== null && filters !== null,
  });
}

// --- crm mutations ---

export function useCreateCustomer() {
  return useApiMutation<CreateCustomerInput, CustomerView>(
    (input, token) =>
      apiFetch<CustomerView>('/crm/customers', { method: 'POST', body: input, token }),
    [['crm']],
  );
}

export function useUpdateCustomer() {
  return useApiMutation<{ customerId: string; input: UpdateCustomerInput }, CustomerView>(
    ({ customerId, input }, token) =>
      apiFetch<CustomerView>(`/crm/customers/${customerId}`, {
        method: 'PATCH',
        body: input,
        token,
      }),
    [['crm']],
  );
}

export function useDeleteCustomer() {
  return useApiMutation<string, void>(
    (customerId, token) =>
      apiFetch<void>(`/crm/customers/${customerId}`, { method: 'DELETE', token }),
    [['crm']],
  );
}

export function useCreateDeal() {
  return useApiMutation<CreateDealInput, DealView>(
    (input, token) => apiFetch<DealView>('/crm/deals', { method: 'POST', body: input, token }),
    [['crm']],
  );
}

export function useUpdateDeal() {
  return useApiMutation<{ dealId: string; input: UpdateDealInput }, DealView>(
    ({ dealId, input }, token) =>
      apiFetch<DealView>(`/crm/deals/${dealId}`, { method: 'PATCH', body: input, token }),
    [['crm']],
  );
}

// --- org invitations & user management (admin) ---

export function useInvitations() {
  return useAuthedQuery<InvitationView[]>(['org', 'invitations'], '/org/invitations');
}

export function useInviteUser() {
  return useApiMutation<{ email: string; roleId: string }, InvitationView>(
    (input, token) =>
      apiFetch<InvitationView>('/org/invitations', { method: 'POST', body: input, token }),
    [['org']],
  );
}

export function useRevokeInvitation() {
  return useApiMutation<string, void>(
    (invitationId, token) =>
      apiFetch<void>(`/org/invitations/${invitationId}`, { method: 'DELETE', token }),
    [['org']],
  );
}

export function useUpdateUserRoles() {
  return useApiMutation<{ userId: string; roleIds: string[] }, OrgUserView>(
    ({ userId, roleIds }, token) =>
      apiFetch<OrgUserView>(`/org/users/${userId}/roles`, {
        method: 'PUT',
        body: { roleIds },
        token,
      }),
    [['org']],
  );
}

export function useUpdateUserStatus() {
  return useApiMutation<{ userId: string; status: 'active' | 'disabled' }, OrgUserView>(
    ({ userId, status }, token) =>
      apiFetch<OrgUserView>(`/org/users/${userId}/status`, {
        method: 'PUT',
        body: { status },
        token,
      }),
    [['org']],
  );
}

// --- auth ---

export interface LoginCredentials {
  email: string;
  password: string;
}

export function useLogin() {
  const { login } = useAuth();

  return useMutation({
    mutationFn: ({ email, password }: LoginCredentials) => login(email, password),
  });
}

export type { ApiFetchOptions, PaginatedResult };
